import { matchPath } from 'react-router-dom';
import Caller from '../../domain/Api/Caller';
import FormGatewayInterface from '../../domain/Form/FormGatewayInterface';
import Forms from '../../domain/Form/Forms';
import { roleBackOfficeList, ERole } from '../../domain/User/Erole';
import storage from '../../presentation/util/storage';

export default class FormGateway implements FormGatewayInterface {
    private explicitFolderId: string | null;

    constructor(folderId?: string | null) {
        // Store explicit folderId if provided, otherwise will be resolved dynamically
        this.explicitFolderId = folderId ?? null;
    }

    private static getFolderIdFromUrl(): string | null {
        if (typeof window === 'undefined') return null;
        const match = matchPath<{ dossierId?: string }>(window.location.pathname, {
            path: '/dossier/:dossierId',
        });
        return match?.params?.dossierId ?? null;
    }

    private getFolderId(): string | null {
        // Use explicit folderId if provided, otherwise resolve from URL dynamically
        return this.explicitFolderId ?? FormGateway.getFolderIdFromUrl();
    }

    private getKey(baseKey: string): string {
        const folderId = this.getFolderId();
        return folderId ? `${folderId}_${baseKey}` : baseKey;
    }

    save(data: Array<string>): Promise<unknown> {
        return new Promise((resolve) => {
            const formString = localStorage.getItem(this.getKey('form'))
            const formObject = (formString === null) ? {} : JSON.parse(formString)

            Object.assign(formObject, data);
            localStorage.setItem(this.getKey('form'), JSON.stringify(formObject))
            resolve(formObject)
        }).then(response => response);
    }

    updateState(state: string): void {
        const userRole = localStorage.getItem('role')
        if (state !== undefined) {
            if (!userRole || !roleBackOfficeList.includes(userRole) || storage.getFormId(this.getFolderId()) === storage.getBackOfficeFormId()) {
                localStorage.setItem(this.getKey('dossier_state'), state)
            }
        }
    }

    find(): Promise<null | Record<string, unknown>> {
        return new Promise<null | Record<string, unknown>>((resolve) => {
            const formString = localStorage.getItem(this.getKey('form'));
            const formObject = (formString === null) ? {} : JSON.parse(formString);
            localStorage.setItem(this.getKey('form'), JSON.stringify(formObject));
            resolve(formObject);
        }).then(response => response);
    }

    getCurrentValueForFormId(): { values: Record<string, unknown>, direct: boolean, dossier_state: string } {
        const formString = localStorage.getItem(this.getKey('form'));
        const formObject = (formString === null) ? {} : JSON.parse(formString);

        return {
            'values': formObject,
            'direct': (localStorage.getItem(this.getKey('direct')) === 'true'),
            'dossier_state': localStorage.getItem(this.getKey('dossier_state')) || ''
        }
    }

    getValidation(): Promise<unknown> {
        return new Promise((resolve => {
            const formString = localStorage.getItem(this.getKey('validate'));
            const formObject = (formString === null) ? {} : JSON.parse(formString);
            resolve(Object.entries(formObject))
        })).then(response => response)
    }

    getValueForBlockId(blockId: string): string | null {
        const formString = localStorage.getItem(this.getKey('form'));
        const formObject = (formString === null) ? {} : JSON.parse(formString);

        return (formObject[blockId]) ?? null
    }

    getValueForElementId(elementId: string): string|null {
        const formString = localStorage.getItem(this.getKey('form'));
        const formObject = (formString === null) ? {} : JSON.parse(formString);

        return '' !== elementId ? formObject[elementId] ?? null : null;
    }

    getForms(): Promise<Forms[]> {
        return Caller.executeGet(`${Caller.initApiUrl()}/forms`, {})
            .then(data => {
                if (!data) {
                    return []
                }
                return (data.forms).map((form) => {
                    return new Forms(
                        form.id,
                        form.label,
                        form.product_choice_element_id,
                        form.signature_block_id,
                        form.completeness_type
                    )
                })
            })
    }
}
